import { useEffect, useState } from "react";
import { Inbox, Loader2, MailCheck, MailX, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { formatDateTime, formatNumber } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

export default function Postbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/postbox");
      setItems(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const poll = async () => {
    setPolling(true);
    try {
      const r = await api.post("/postbox/poll");
      toast.success(`Postbox aktualisiert (${r.data.pending_count} wartend)`);
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setPolling(false);
    }
  };

  const accept = async (id) => {
    try {
      const r = await api.post(`/reports/accept-pending/${id}`);
      toast.success(
        `Report ${r.data.po_number} importiert (${r.data.entry_count} Einträge)`
      );
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const reject = async (id) => {
    const reason = window.prompt("Grund der Ablehnung (optional):");
    try {
      await api.post(`/postbox/${id}/reject`, { reason });
      toast.success("Pending Report abgelehnt");
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eintrag aus Postbox entfernen?")) return;
    try {
      await api.delete(`/postbox/${id}`);
      toast.success("Eintrag entfernt");
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const pending = items.filter((i) => i.status === "pending");
  const history = items.filter((i) => i.status !== "pending");

  return (
    <div data-testid="postbox-page">
      <PageHeader
        title="Postbox"
        subtitle="Eingegangene Reports"
        actions={
          <Button
            onClick={poll}
            disabled={polling}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="postbox-poll-btn"
          >
            {polling ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw size={16} className="mr-2" />
            )}
            Postfach prüfen
          </Button>
        }
      >
        Eingehende E-Mails mit Excel-Anhang werden automatisch alle 10 Minuten gezogen.
        Akzeptiere oder verwerfe Reports vor der Übernahme ins Dashboard.
      </PageHeader>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 space-y-6">
        <SectionTable
          title={`Wartend (${pending.length})`}
          items={pending}
          loading={loading}
          emptyLabel="Keine wartenden Reports."
          renderActions={(it) => (
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => accept(it.id)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900"
                data-testid={`pending-accept-${it.id}`}
              >
                <MailCheck size={14} /> Akzeptieren
              </button>
              <button
                onClick={() => reject(it.id)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 hover:text-rose-900"
                data-testid={`pending-reject-${it.id}`}
              >
                <MailX size={14} /> Ablehnen
              </button>
            </div>
          )}
        />

        <SectionTable
          title={`Verlauf (${history.length})`}
          items={history}
          loading={false}
          emptyLabel="Noch keine bearbeiteten Postbox-Einträge."
          renderActions={(it) => (
            <button
              onClick={() => remove(it.id)}
              className="text-slate-400 hover:text-rose-600"
              data-testid={`postbox-delete-${it.id}`}
            >
              <Trash2 size={14} />
            </button>
          )}
        />
      </div>
    </div>
  );
}

function SectionTable({ title, items, loading, emptyLabel, renderActions }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Inbox size={16} className="text-slate-500" />
          <h3 className="font-display text-lg font-bold">{title}</h3>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
            <th className="text-left px-4 py-2 font-semibold">Empfangen</th>
            <th className="text-left px-4 py-2 font-semibold">Absender</th>
            <th className="text-left px-4 py-2 font-semibold">Betreff</th>
            <th className="text-left px-4 py-2 font-semibold">Datei</th>
            <th className="text-right px-4 py-2 font-semibold">Größe</th>
            <th className="text-left px-4 py-2 font-semibold">Status</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Lade …
              </td>
            </tr>
          )}
          {!loading && items.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                {emptyLabel}
              </td>
            </tr>
          )}
          {items.map((it) => (
            <tr key={it.id} className="border-b border-slate-100" data-testid={`pending-row-${it.id}`}>
              <td className="px-4 py-2 text-slate-500">{formatDateTime(it.received_at)}</td>
              <td className="px-4 py-2">
                <div className="text-slate-900 text-sm">{it.from_name || it.from_email || "—"}</div>
                <div className="text-xs text-slate-500">{it.from_email}</div>
              </td>
              <td className="px-4 py-2">{it.subject || "—"}</td>
              <td className="px-4 py-2 font-mono text-xs">{it.filename}</td>
              <td className="px-4 py-2 text-right font-mono text-xs text-slate-500">
                {formatNumber((it.file_size || 0) / 1024)} KB
              </td>
              <td className="px-4 py-2">
                <StatusBadge status={it.status} />
              </td>
              <td className="px-4 py-2 text-right">{renderActions ? renderActions(it) : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
